/**
 * Syllable Counter — heuristic syllable counting.
 *
 * Pure, dictionary-free heuristic (vowel-group counting with silent-ending
 * removal). It's the standard approach used by online counters and is accurate
 * for ~85% of English words — good enough for poetry/haiku helpers, and clearly
 * labeled as an estimate in the UI.
 */

const VOWEL_GROUPS = /[aeiouy]+/g;

/** Estimate the number of syllables in a single word. */
export function countSyllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return 0;
  if (w.length <= 3) return 1;

  // Strip common silent endings (silent "e", "es", "ed") and a leading "y".
  const stripped = w
    .replace(/(?:[^laeiouy]es|[^laeiouy]e|ed)$/, "")
    .replace(/^y/, "");

  const groups = stripped.match(VOWEL_GROUPS);
  return Math.max(1, groups ? groups.length : 1);
}

export interface LineCount {
  text: string;
  words: number;
  syllables: number;
}

export interface TextCount {
  syllables: number;
  words: number;
  characters: number;
  lines: LineCount[];
}

/** Count syllables, words, and characters across a block of text, per line. */
export function countText(text: string): TextCount {
  const lines = text.split("\n").map((line) => {
    const words = line.trim().split(/\s+/).filter(Boolean);
    return {
      text: line,
      words: words.length,
      syllables: words.reduce((sum, w) => sum + countSyllables(w), 0),
    };
  });

  return {
    syllables: lines.reduce((sum, l) => sum + l.syllables, 0),
    words: lines.reduce((sum, l) => sum + l.words, 0),
    characters: text.length,
    lines,
  };
}

/** Haiku check: three lines following the 5-7-5 syllable pattern. */
export function isHaiku(text: string): boolean {
  const pattern = [5, 7, 5];
  const lines = countText(text).lines.filter((l) => l.words > 0);
  if (lines.length !== 3) return false;
  return lines.every((l, i) => l.syllables === pattern[i]);
}
